/**
 * Drop-in Tamagui Card with optional top frost strip.
 * Opt in: frost (or frost={true}).
 */
import React, { forwardRef } from 'react';
import { Card as TamaguiCardBase } from 'tamagui';
import CardFrostStrip from './CardFrostStrip';
import type { FrostColors, FrostTone } from './frostTokens';

export type AppCardProps = React.ComponentProps<typeof TamaguiCardBase> & {
  frost?: boolean;
  frostTone?: FrostTone;
  frostColors?: FrostColors | string[];
  frostCompact?: boolean;
  frostHeight?: number;
};

/** True when bg looks like an intentional color override (skip default frost). */
export function isCustomCardSurface(backgroundColor: unknown): boolean {
  if (backgroundColor == null || backgroundColor === true) return false;
  const s = String(backgroundColor).trim().toLowerCase();
  if (!s || s === 'transparent') return false;
  if (s.startsWith('$')) return false;
  // Default / theme surface whites & dark slate shells → keep frost
  if (s === 'white' || s.includes('#fff') || s.includes('ffffff')) return false;
  if (s.includes('255, 255, 255') || s.includes('255,255,255')) return false;
  if (s.includes('1e293b') || s.includes('0f172a') || s.includes('30, 41, 59') || s.includes('40, 40, 40')) return false;
  if (s.includes('rgba(255') && (s.includes('0.9') || s.includes('0.95'))) return false;
  if (s.includes('rgba(40') || s.includes('rgba(30, 41')) return false;
  // Everything else (muted nested chips, status colors, brand fills) → no frost
  return true;
}

type CardComponent = React.ForwardRefExoticComponent<
  AppCardProps & React.RefAttributes<any>
> & {
  Header: typeof TamaguiCardBase.Header;
  Footer: typeof TamaguiCardBase.Footer;
  Background: typeof TamaguiCardBase.Background;
  Title: typeof TamaguiCardBase.Title;
  Description: typeof TamaguiCardBase.Description;
  Content?: typeof TamaguiCardBase.Content;
};

const Card = forwardRef(function AppCard(
  {
    frost,
    frostTone = 'default',
    frostColors,
    frostCompact,
    frostHeight,
    children,
    overflow,
    backgroundColor,
    ...rest
  }: AppCardProps,
  ref: React.Ref<any>,
) {
  const frostEnabled = frost === true;

  return (
    <TamaguiCardBase
      ref={ref}
      backgroundColor={backgroundColor}
      overflow={overflow ?? (frostEnabled ? 'hidden' : overflow)}
      {...rest}
    >
      {frostEnabled ? (
        <CardFrostStrip
          tone={frostTone}
          colors={frostColors}
          compact={frostCompact}
          height={frostHeight}
          enabled
        />
      ) : null}
      {children}
    </TamaguiCardBase>
  );
}) as CardComponent;

Card.Header = TamaguiCardBase.Header;
Card.Footer = TamaguiCardBase.Footer;
Card.Background = TamaguiCardBase.Background;
Card.Title = TamaguiCardBase.Title;
Card.Description = TamaguiCardBase.Description;
if (TamaguiCardBase.Content) {
  Card.Content = TamaguiCardBase.Content;
}

export { Card };
export default Card;
